import cx from 'classnames';
import { useCallback, useContext, useRef, type FunctionComponent } from 'react';

import { useIsTemplateCard } from '@trello/business-logic-react/card';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useCardId } from '@trello/id-context';
import { useIsInboxBoard } from '@trello/personal-workspace';
import { useSharedStateSelector } from '@trello/shared-state';

import { useIsCardMarkedComplete } from 'app/src/components/CardDoneState/useIsCardMarkedComplete';
import { useIsInboxAutoArchiveEnabled } from 'app/src/components/CardDoneState/useIsInboxAutoArchiveEnabled';
import type { CardType } from 'app/src/components/CardType';
import { MirrorCardActions } from 'app/src/components/MirrorCard';
import { activeCardSharedState } from './activeCardSharedState';
import { CardFrontArchiveButton } from './CardFrontArchiveButton';
import { useCardFrontCardInfoFragment } from './CardFrontCardInfoFragment.generated';
import { CardFrontContext } from './CardFrontContext';
import { CardFrontEditButton } from './CardFrontEditButton';

// eslint-disable-next-line @trello/less-matches-component
import styles from './CardFront.less';

interface CardFrontActionsProps {
  cardType: CardType;
}

export const CardFrontActions: FunctionComponent<CardFrontActionsProps> = ({
  cardType,
}) => {
  const { value: isMirrorCardsEnabled } = useFeatureGate('xf_mirror_cards');

  const { editButtonRef } = useContext(CardFrontContext);

  const cardId = useCardId();
  const isCardActive = useSharedStateSelector(
    activeCardSharedState,
    useCallback(({ idActiveCard }) => idActiveCard === cardId, [cardId]),
  );

  const { data: card } = useCardFrontCardInfoFragment({
    from: { id: cardId },
  });

  const { value: isNewCardDoneStateFeatureEnabled } = useFeatureGate(
    'phx_new_card_done_state_workspace',
  );

  const isComplete = useIsCardMarkedComplete(cardId);
  const isArchived = card?.closed;
  const isTemplate = useIsTemplateCard(cardId);
  const isInbox = useIsInboxBoard();
  const isInboxAutoArchiveEnabled = useIsInboxAutoArchiveEnabled();
  const previousIsComplete = useRef(isComplete);

  // Determine if the archive button should be hidden while auto archiving *before* we update the ref
  const shouldHideArchiveButtonWhileAutoArchiving =
    isComplete &&
    !previousIsComplete.current &&
    isInbox &&
    isInboxAutoArchiveEnabled;

  previousIsComplete.current = isComplete;

  const isArchiveButtonVisible =
    isNewCardDoneStateFeatureEnabled &&
    !shouldHideArchiveButtonWhileAutoArchiving &&
    isComplete &&
    !isArchived &&
    !isTemplate &&
    cardType !== 'separator';

  const isEditButtonVisible = !isArchived;
  const canShowArchiveButtonOnMirrorCard = !isArchived;

  return (
    <div
      className={cx(
        styles.cardFrontActions,
        isCardActive && styles['cardFrontActions--isCardActive'],
      )}
    >
      {isEditButtonVisible && <CardFrontEditButton ref={editButtonRef} />}
      {isArchiveButtonVisible && <CardFrontArchiveButton />}
      {cardType === 'mirror' && isMirrorCardsEnabled && (
        <MirrorCardActions
          canShowArchiveButton={canShowArchiveButtonOnMirrorCard}
        />
      )}
    </div>
  );
};
